import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/layout/PublicHeader";
import {
  ArrowRight, Award, Briefcase, ShieldCheck, Sparkles, Target, MessageSquare, Check, Globe,
} from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";

// Interactive 3D globe for the hero — lazy-loaded so the hero text paints
// before the ~250kB three.js bundle arrives.
const HireGlobe = lazy(() => import("@/components/HireGlobe"));

// Country picker — KSA-first, then GCC, Levant, then top labour-export countries
// that Maqam's agency pipeline imports from. Selecting one pre-applies the
// Country filter inside RecruiterDashboard (passed via ?country=…).
const HIRE_COUNTRIES = [
  "Saudi Arabia", "United Arab Emirates", "Qatar", "Kuwait", "Bahrain", "Oman",
  "Egypt", "Jordan", "Lebanon", "Morocco", "Tunisia",
  "France", "Germany", "Italy", "United Kingdom", "Romania",
  "India", "Pakistan", "Bangladesh", "Philippines", "Sri Lanka", "Nepal",
];

// European subset — KSA Vision 2030 mega-projects (NEOM / Red Sea / Qiddiya)
// actively sponsor relocation for these nationalities, so the globe pulses
// Saudi Arabia and the hero shows a "HIGH DEMAND" badge when one is picked.
const EU_HIRE_COUNTRIES = new Set([
  "France", "Germany", "Italy", "United Kingdom", "Romania",
]);

const VALUE_PROPS = [
  {
    icon: Sparkles,
    title: "Search a pre-vetted talent pool",
    body:
      "Every candidate on Maqam has a Maqam Score — a composite 0-100 grade built from real skill tests, mock-interview transcripts, and CV depth. No more shouting into a LinkedIn void.",
  },
  {
    icon: Target,
    title: "Filter by what actually matters",
    body:
      "Maqam Score, skill tier (Platinum / Diamond / Gold), interview score, Nitaqat fit, languages, preferred city, years of experience — all in one search bar.",
  },
  {
    icon: ShieldCheck,
    title: "PDPL-compliant by design",
    body:
      "Profiles are anonymized by default. Real name + contact only unlock when the candidate accepts your intro request. Every outreach is routed through Maqam's proxy — no leaked emails, no scraping.",
  },
  {
    icon: MessageSquare,
    title: "Intro requests, not cold emails",
    body:
      "Send a short pitch through Maqam. The candidate sees it in their private inbox and can accept, decline, or counter. You only get contact details when there's mutual interest.",
  },
];

const STEPS = [
  { num: "01", title: "Sign up your company", body: "Verified employer badge attached to your brand profile in <24 h." },
  { num: "02", title: "Search the KSA talent pool", body: "Unlimited filters, ranked by Maqam Score & Nitaqat compatibility." },
  { num: "03", title: "Send intro requests", body: "50 / month included. Candidates accept → contact reveals. Overage at $25 / intro." },
  { num: "04", title: "Hire & onboard", body: "Once the candidate accepts, message + book interview directly inside Maqam." },
];

export default function Hire() {
  useSEO({
    title: "Hire on Maqam — Reach Saudi Arabia's top job-ready talent",
    description:
      "Maqam's Hire side gives employers a PDPL-compliant search engine over Saudi Arabia's most engaged talent pool. Filter by Maqam Score, skill tier, languages — and send intro requests through our privacy proxy.",
    path: "/hire",
  });

  const [plan, setPlan] = useState(null);
  const [country, setCountry] = useState("");
  // Live count of active candidates currently looking for work (fed by the
  // HireGlobe component once it loads the /hire/active-talent-map data).
  const [activeCount, setActiveCount] = useState(null);

  useEffect(() => {
    api.get("/employer/plans").then((r) => {
      const p = (r.data?.plans || [])[0];
      if (p) setPlan(p);
    }).catch(() => {});
  }, []);

  // Primary CTA target — when a country is picked, send the recruiter
  // straight to the candidate-pool view, pre-filtered. Otherwise the
  // standard "Become an employer" registration flow.
  const primaryCtaHref = country
    ? `/recruiter/search?country=${encodeURIComponent(country)}`
    : "/recruiter/register";
  const primaryCtaLabel = country ? `Browse ${country} talent` : "Become an employer";

  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white" data-testid="hire-page">
      <PublicHeader />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 100% 85% at 50% 45%, #e6e6eb 0%, #b8b8be 25%, #6e6e74 55%, #2a2a2e 90%)",
        }}
      >
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 lg:pb-32">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-12 items-center">
            {/* LEFT — copy + CTAs */}
            <div className="order-2 lg:order-1">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                / FOR EMPLOYERS · KSA TALENT MARKETPLACE
              </div>
              <h1 className="font-display font-black text-5xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.9]">
                Stop posting jobs.
                <br />
                <span className="italic text-white/70">Start picking.</span>
              </h1>
              <p className="mt-7 text-lg md:text-xl text-white/70 leading-relaxed max-w-xl">
                Reverse the funnel. Maqam's Hire side is a search engine over Saudi
                Arabia's most engaged talent pool — every candidate scored,
                verified, and ready to be approached. <span className="text-white">$700/month, flat.</span>
              </p>

              {/* Selected-country status pill — updates live as you click the globe */}
              <div className="mt-8 flex items-center gap-3" data-testid="hire-globe-status">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Selected:
                </span>
                {country ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF9500] text-black font-bold text-sm">
                    <Globe className="w-3.5 h-3.5" />
                    {country}
                    <button
                      type="button"
                      onClick={() => setCountry("")}
                      className="ms-1 hover:bg-black/15 px-1 leading-none"
                      data-testid="hire-globe-clear"
                      aria-label="Clear country"
                    >×</button>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-white/60 font-mono text-xs uppercase tracking-widest">
                    Spin the globe → click a glowing country
                  </span>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-4 items-stretch">
                {/* Country picker dropdown — accessibility fallback for users
                    who can't / don't want to use the 3D globe (keyboard nav,
                    reduced motion, mobile pinch). */}
                <label className="inline-flex items-center gap-3 bg-white/[0.04] border border-white/15 hover:border-white/40 px-5 py-5 transition-colors min-w-[200px]">
                  <Globe className="w-4 h-4 text-white/55 flex-shrink-0" />
                  <span className="sr-only">Country</span>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="bg-transparent text-white font-bold text-sm outline-none flex-1 cursor-pointer"
                    data-testid="hire-country-picker"
                  >
                    <option value="" className="bg-[#141416]">Any country</option>
                    {HIRE_COUNTRIES.map((c) => (
                      <option key={c} value={c} className="bg-[#141416]">{c}</option>
                    ))}
                  </select>
                </label>
                <Link
                  to={primaryCtaHref}
                  className="inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-white/85 px-8 py-5 font-bold text-base transition-colors"
                  data-testid="hire-cta-signup"
                >
                  {primaryCtaLabel} <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-white px-8 py-5 font-bold text-base transition-colors"
                  data-testid="hire-cta-see-pricing"
                >
                  See pricing
                </a>
              </div>
              <div className="mt-3 flex flex-wrap gap-4">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/recruiters`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-[#FF9500] transition-colors"
                  data-testid="hire-cta-download-doc"
                >
                  📄 Download recruiter overview (PDF)
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/investors`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 hover:text-[#FF9500] transition-colors"
                  data-testid="hire-cta-download-investor-doc"
                >
                  💼 Investor brief (PDF)
                </a>
              </div>
            </div>

            {/* RIGHT — interactive 3D globe */}
            <div className="order-1 lg:order-2 relative">
              {/* Globe sits naturally on the silver stage — no inner glow
                  needed since the page background already provides one. */}
              <Suspense fallback={
                <div className="w-full aspect-square max-w-[560px] mx-auto flex items-center justify-center text-white/40 font-mono text-xs uppercase tracking-widest">
                  Loading globe…
                </div>
              }>
                <HireGlobe
                  hireCountries={HIRE_COUNTRIES}
                  selected={country}
                  onCountrySelect={setCountry}
                  onActiveCountChange={setActiveCount}
                />
              </Suspense>
              <div className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 flex items-center justify-center gap-3 flex-wrap">
                <span>22 countries · click any glowing region</span>
                {activeCount !== null && activeCount > 0 && (
                  <span className="inline-flex items-center gap-1.5" data-testid="hire-active-count">
                    <span className="relative inline-flex w-1.5 h-1.5">
                      <span className="absolute inset-0 bg-[#FF9500] rounded-full animate-ping opacity-75" />
                      <span className="relative inline-flex w-1.5 h-1.5 bg-[#FF9500] rounded-full" />
                    </span>
                    <span className="text-[#FF9500]">live talent flow</span>
                  </span>
                )}
                {country && EU_HIRE_COUNTRIES.has(country) && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#FF9500] text-black font-bold tracking-[0.2em]"
                    data-testid="hire-high-demand"
                  >
                    ⚡ KSA HIGH DEMAND FOR {country.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 max-w-3xl">
            {[
              { num: "0-100", label: "Maqam Score per candidate" },
              { num: "$700", label: "/ month, flat" },
              { num: "50", label: "intro requests included" },
              { num: "PDPL", label: "compliant by design" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">{s.num}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="border-t border-white/10 bg-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-12 mb-16">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ WHY MAQAM</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                We don't sell ads.
                <br />
                <span className="italic text-white/60">We sell outcomes.</span>
              </h2>
            </div>
            <p className="text-lg text-white/70 leading-relaxed lg:pt-20">
              LinkedIn shows you 5,000 résumés that may or may not be real.
              Bayt charges per post and prays. Maqam ranks the entire talent
              pool the way you would — by who can actually do the job.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="border border-white/10 p-8 bg-[#141416] hover:border-white/30 transition-colors">
                <v.icon className="w-8 h-8 text-white mb-5" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-2xl mb-3 tracking-tight">{v.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-4">/ HOW IT WORKS</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-16 max-w-2xl">
            Four steps from sign-up to signed offer.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10">
            {STEPS.map((s, i) => (
              <div
                key={s.num}
                className={`p-8 ${i < STEPS.length - 1 ? "border-b lg:border-b-0 lg:border-r border-white/10" : ""} hover:bg-white/[0.02] transition-colors`}
              >
                <div className="font-display font-black text-5xl tracking-tighter text-white/20 mb-6">{s.num}</div>
                <h3 className="font-display font-bold text-xl mb-3 tracking-tight">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAQAM SCORE EXPLAINER */}
      <section className="border-t border-white/10 bg-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ THE MAQAM SCORE</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-6">
                One number.
                <br />
                <span className="italic text-white/60">Real signal.</span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                Every Maqam candidate has a composite Maqam Score (0-100) built
                from real, anti-cheat-monitored signal — not self-reported
                bullshit.
              </p>
              <ul className="space-y-4">
                {[
                  ["30%", "Skill-test tier — Platinum (98+), Diamond (92+), Gold (85+), proctored & paste-detected"],
                  ["30%", "AI Mock Interview average — last 5 sessions, voice-transcribed & scored by Claude Sonnet"],
                  ["20%", "Profile depth — headline, experience, languages, preferred cities, etc."],
                  ["20%", "CV completeness — length, structure, skills coverage"],
                ].map(([pct, label]) => (
                  <li key={label} className="flex items-start gap-4">
                    <span className="font-display font-black text-3xl tracking-tighter text-white/40 min-w-[60px]">{pct}</span>
                    <span className="text-white/80 text-sm leading-relaxed pt-2">{label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sample candidate card */}
            <div className="border border-white/15 bg-[#141416] p-8 shadow-2xl">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-4">SAMPLE CANDIDATE</div>
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="font-display font-bold text-2xl">Faisal A.</div>
                  <div className="text-white/60 text-sm">Senior Backend Engineer · Riyadh</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">Maqam Score</div>
                  <div className="font-display font-black text-5xl tracking-tighter text-[#34C759]">92</div>
                </div>
              </div>
              <div className="space-y-3 mb-6">
                {[
                  ["Skill tier",       "Platinum · Python",  98],
                  ["Interview avg",    "5 sessions · 89/100", 89],
                  ["Profile depth",    "Complete",            95],
                  ["CV depth",         "1.4k chars · 12 skills", 88],
                ].map(([k, v, val]) => (
                  <div key={k} className="flex items-center gap-3 text-xs">
                    <span className="w-24 text-white/60">{k}</span>
                    <div className="flex-1 h-1.5 bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#34C759]" style={{ width: `${val}%` }} />
                    </div>
                    <span className="w-20 text-right text-white/70">{v}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-4 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
                <span className="px-2 py-1 border border-[#8b5cf6] text-[#8b5cf6]">Platinum · Python</span>
                <span className="px-2 py-1 border border-[#06b6d4] text-[#06b6d4]">Diamond · AWS</span>
                <span className="px-2 py-1 border border-white/30 text-white/70">Saudi · ar / en</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="text-center mb-16">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">/ PRICING</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">
              One tier. No surprises.
            </h2>
            <p className="text-white/60 text-lg">
              {plan ? `Cancel anytime. Billed in ${plan.currency || "USD"}.` : "Cancel anytime."}
            </p>
          </div>

          <div className="max-w-2xl mx-auto border-2 border-white p-10 lg:p-16 bg-[#141416] relative">
            <div className="absolute -top-4 left-10 px-3 py-1 bg-white text-black font-mono text-[10px] uppercase tracking-widest">
              Employer Pro
            </div>

            <div className="flex items-baseline gap-3 mb-2">
              <div className="font-display font-black text-7xl lg:text-8xl tracking-tighter">
                ${plan?.price_monthly_usd || 700}
              </div>
              <div className="font-mono text-sm uppercase tracking-widest text-white/50">/ month</div>
            </div>
            <p className="text-white/60 mb-10 text-sm">Flat rate. No per-post fees. No long-term contract.</p>

            <ul className="space-y-4 mb-10">
              {(plan?.includes || [
                "Unlimited candidate search across the KSA talent pool",
                "50 direct intro requests per month (overage $25 / intro)",
                "Filter by Maqam Score, skill tier, languages, Nitaqat fit",
                "Anonymized profiles by default; identity reveals only on candidate accept",
                "All outreach routed through Maqam's privacy proxy — PDPL-compliant",
                "Company brand profile + verified employer badge",
              ]).map((feat) => (
                <li key={feat} className="flex items-start gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#34C759] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-sm leading-relaxed">{feat}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/recruiter/register"
              className="block w-full text-center bg-white text-black hover:bg-white/85 px-8 py-5 font-bold text-base transition-colors"
              data-testid="hire-pricing-cta"
            >
              Become an employer →
            </Link>
            <p className="mt-4 text-center text-xs text-white/40">
              Need a multi-recruiter plan? <a href="mailto:hello@maqam-ksa.com" className="text-white/70 hover:text-white underline">Contact us</a>.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER STRIP */}
      <section className="border-t border-white/10 bg-[#1a1a1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-white/40" />
            <span className="font-mono text-xs uppercase tracking-widest text-white/50">
              Trusted by candidates from Aramco · STC · NEOM · PIF
            </span>
          </div>
          <Link
            to="/recruiter/register"
            className="inline-flex items-center gap-3 text-white hover:text-white/70 transition-colors font-bold"
          >
            Start your free verification <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
